import { useEffect, useState } from 'react'
import { createInvite, type FamilySession } from '@core/session'
import { Button, Card, ErrorNote } from '@design/components'
import { PushSetting } from '@features/settings/PushSetting'
import { DigestSetting } from '@features/settings/DigestSetting'
import { ExportSetting } from '@features/settings/ExportSetting'
import { BankSetting } from '@features/settings/BankSetting'
import { ProblemsSetting } from '@features/settings/ProblemsSetting'
import { SpaceSettingsCard } from '@features/settings/SpaceSettings'
import { NewSpace } from '@features/settings/NewSpace'
import { loadSharedSpaces } from '@core/expenses'
import { ShareSetting } from '@features/settings/ShareSetting'
import { RecurringScreen } from '@features/recurring/RecurringScreen'
import { TrashScreen } from '@features/trash/TrashScreen'
import { Overlay } from '@design/Overlay'

/**
 * Everything visited monthly rather than daily lives here, which is what
 * removes the need for a More tab (docs/spec.md §30). Phase 2 ships only the
 * parts that exist: who is in the family, inviting a parent, signing out.
 */
export function ProfileSheet({
  session,
  onClose,
  onSignOut,
}: {
  session: FamilySession
  onClose: () => void
  onSignOut: () => void
}) {
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [recurring, setRecurring] = useState(false)
  const [trash, setTrash] = useState(false)
  const [sharedIds, setSharedIds] = useState<string[]>([])
  const [spacesKey, setSpacesKey] = useState(0)

  const isAdmin = session.role === 'admin'

  useEffect(() => {
    loadSharedSpaces()
      .then((all) => setSharedIds(all.map((s) => s.id)))
      .catch(() => setSharedIds([]))
  }, [spacesKey])

  async function invite() {
    setBusy(true)
    setError('')
    try {
      setCode(await createInvite('Ouder'))
    } catch {
      setError('Uitnodigen lukte niet.')
    }
    setBusy(false)
  }

  return (
    <Overlay>
      <header className="shrink-0 pad-safe-top px-4 pb-3 flex items-center justify-between border-b border-line">
        <h2 className="text-lg font-semibold">Instellingen</h2>
        <button onClick={onClose} className="text-muted px-2 py-1" aria-label="Sluiten">
          Klaar
        </button>
      </header>

      <div className="flex-1 scroll-area px-4 py-4 pad-safe-bottom">
        <PushSettingSlot userId={session.userId} />

        {isAdmin && <ProblemsSetting />}

        <DigestSetting userId={session.userId} />

        <ShareSetting />

        <Card className="mb-4">
          <p className="font-medium">{session.displayName || 'Naamloos'}</p>
          <p className="text-sm text-muted">{session.email}</p>
          <p className="text-sm text-muted mt-2">
            {session.accountName} · {isAdmin ? 'Beheerder' : 'Familielid'}
          </p>
        </Card>

        {isAdmin && (
          <Card className="mb-4">
            <p className="font-medium mb-1">Iemand uitnodigen</p>
            <p className="text-sm text-muted mb-3">
              Geef deze code door. Je ouder vult hem in bij ‘Ik heb een code’.
            </p>

            {code ? (
              <div className="text-center py-3">
                <p className="text-3xl font-semibold tracking-[0.2em]">{code}</p>
                <p className="text-xs text-muted mt-2">Geldig gedurende 14 dagen.</p>
              </div>
            ) : (
              <>
                <ErrorNote>{error}</ErrorNote>
                <Button tone="secondary" onClick={invite} disabled={busy}>
                  {busy ? 'Bezig…' : 'Maak een code'}
                </Button>
              </>
            )}
          </Card>
        )}

        {isAdmin && (
          <Card className="mb-4">
            <p className="font-medium mb-1">Vaste kosten</p>
            <p className="text-sm text-muted mb-3">
              Huur, elektriciteit, verzekering. Ze verschijnen vanzelf in de
              kalender zonder dat iemand ze fotografeert.
            </p>
            <Button tone="secondary" onClick={() => setRecurring(true)}>
              Bekijken
            </Button>
          </Card>
        )}

        {isAdmin && <ExportSetting />}

        {isAdmin && (
          <Card className="mb-4">
            <p className="font-medium mb-1">Prullenbak</p>
            <p className="text-sm text-muted mb-3">
              Wat je weggooit blijft hier 30 dagen staan.
            </p>
            <Button tone="secondary" onClick={() => setTrash(true)}>
              Openen
            </Button>
          </Card>
        )}

        {isAdmin && <BankSetting />}

        {isAdmin ? (
          <>
            <p className="text-sm text-muted mb-2">Ruimtes</p>
            {session.spaces.map((space) => (
              <SpaceSettingsCard
                key={space.id}
                space={space}
                userId={session.userId}
                shared={sharedIds.includes(space.id)}
                onSharedChange={() => setSpacesKey((k) => k + 1)}
              />
            ))}
            <NewSpace
              spaces={session.spaces}
              onCreated={() => window.location.reload()}
            />
          </>
        ) : (
          <Card className="mb-4">
            <p className="font-medium mb-2">Ruimtes</p>
            <ul className="text-sm text-muted space-y-1">
              {session.spaces.map((s) => (
                <li key={s.id}>{s.name}</li>
              ))}
            </ul>
          </Card>
        )}

        <Button tone="quiet" onClick={onSignOut}>
          Afmelden
        </Button>
      </div>

      {trash && <TrashScreen onClose={() => setTrash(false)} />}

      {recurring && (
        <RecurringScreen
          spaces={session.spaces}
          userId={session.userId}
          onClose={() => setRecurring(false)}
        />
      )}
    </Overlay>
  )
}

/** Kept as its own component so the sheet stays under the file-size rule. */
function PushSettingSlot({ userId }: { userId: string }) {
  return <PushSetting userId={userId} />
}
